import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Modal from '../../components/shared/Modal';

const ROLES = ['admin', 'cliente', 'colaborador'];

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formRole, setFormRole] = useState('cliente');
  const [formPassword, setFormPassword] = useState('');

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('agency_profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      showToast('error', `Erro: ${error.message}`);
    } else {
      setUsers(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormEmail('');
    setFormName('');
    setFormRole('cliente');
    setFormPassword('');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Chama a edge function para criar o usuario
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: formEmail,
          password: formPassword,
          full_name: formName,
          role: formRole,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      showToast('success', `Usuario "${formName}" criado`);
      setShowCreate(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      showToast('error', `Erro ao criar: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('agency_profiles')
        .update({
          full_name: formName,
          role: formRole,
        })
        .eq('id', editingUser.id);

      if (error) throw error;

      showToast('success', 'Usuario atualizado');
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (err) {
      showToast('error', `Erro: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      const { error } = await supabase
        .from('agency_profiles')
        .update({ active: !user.active })
        .eq('id', user.id);

      if (error) throw error;
      showToast('success', user.active ? 'Usuario desativado' : 'Usuario ativado');
      fetchUsers();
    } catch (err) {
      showToast('error', `Erro: ${err.message}`);
    }
  };

  const openEdit = (user) => {
    setFormName(user.full_name);
    setFormRole(user.role);
    setFormEmail(user.email);
    setEditingUser(user);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        Carregando usuarios...
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="admin-page-header">
          <div>
            <h1 className="page-title">Gerenciar Usuarios</h1>
            <p className="page-subtitle">{users.length} usuario{users.length !== 1 ? 's' : ''} cadastrado{users.length !== 1 ? 's' : ''}</p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
            <Link to="/admin/permissions" className="btn btn-secondary">Permissoes</Link>
            <button className="btn btn-primary" onClick={() => { resetForm(); setShowCreate(true); }}>
              + Novo Usuario
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.full_name || '—'}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge role-badge--${user.role}`}>{user.role}</span>
                </td>
                <td>
                  <span className={user.active ? 'status-active' : 'status-inactive'}>
                    {user.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(user)}>
                      Editar
                    </button>
                    <button
                      className={`btn btn-sm ${user.active ? 'btn-danger' : 'btn-primary'}`}
                      onClick={() => toggleActive(user)}
                    >
                      {user.active ? 'Desativar' : 'Ativar'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal criar usuario */}
      {showCreate && (
        <Modal title="Novo Usuario" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input
                className="form-input"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Senha *</label>
              <input
                className="form-input"
                type="password"
                value={formPassword}
                onChange={(e) => setFormPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Nome Completo</label>
              <input
                className="form-input"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role *</label>
              <select
                className="form-input"
                value={formRole}
                onChange={(e) => setFormRole(e.target.value)}
              >
                {ROLES.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Criando...' : 'Criar Usuario'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal editar usuario */}
      {editingUser && (
        <Modal title={`Editar ${editingUser.email}`} onClose={() => setEditingUser(null)}>
          <form onSubmit={handleUpdate}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={formEmail} disabled style={{ opacity: 0.6 }} />
            </div>
            <div className="form-group">
              <label className="form-label">Nome Completo</label>
              <input
                className="form-input"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role *</label>
              <select
                className="form-input"
                value={formRole}
                onChange={(e) => setFormRole(e.target.value)}
              >
                {ROLES.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setEditingUser(null)}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Toast */}
      {toast && (
        <div className="toast-container">
          <div className={`toast toast--${toast.type}`}>{toast.message}</div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
